// Copyright (c) 2021 WiseTime. All rights reserved.

import { TimeGroup, Timeline, TimeRow } from "../../generated/wisetime/user/timeline/v4/timeline_pb"
import { equals, uniq } from "ramda"
import { t } from "@lingui/macro"
import { Tag } from "../../generated/legebill/type/tag_pb"
import { TagRef } from "../../generated/wisetime/user/type/tag_ref_pb"
import { TagsCache } from "./slice"
import { SiteId } from "../user-context/slice"
import { TagCategory } from "../../generated/legebill/type/tag_category_pb"

type TimeRowOrTimeGroup = TimeRow.AsObject | TimeGroup.AsObject
export type IdsList = string[]

export const getTagIdsFromTimeline = (timeline: Timeline.AsObject): IdsList => {
  const tagIdsTimeRowsInRange = getTagIdsFromTimeRowsOrTimeGroups(timeline.timeRowsInRangeList)
  const tagIdsTimeGroupsInRange = getTagIdsFromTimeRowsOrTimeGroups(timeline.timeGroupsInRangeList)
  return uniq(tagIdsTimeRowsInRange.concat(tagIdsTimeGroupsInRange))
}

export const getTagIdsFromTimeRowsOrTimeGroups = (timeRowsOrTimeGroups: TimeRowOrTimeGroup[]): IdsList => {
  return uniq(
    timeRowsOrTimeGroups
      .map(timeRowOrTimeGroup => timeRowOrTimeGroup.tagsList.map(tag => tag.id))
      .reduce((acc, tags) => acc.concat(tags), [])
  )
}

export const getTagCategoriesIdsFromTimeline = (timeline: Timeline.AsObject): IdsList => {
  const tagCategoriesIdsTimeRowsInRange = getTagCategoriesIdsFromTimeRowsOrTimeGroups(timeline.timeRowsInRangeList)
  const tagCategoriesIdsTimeGroupsInRange = getTagCategoriesIdsFromTimeRowsOrTimeGroups(timeline.timeGroupsInRangeList)
  return uniq(tagCategoriesIdsTimeRowsInRange.concat(tagCategoriesIdsTimeGroupsInRange))
}

export const getTagCategoriesIdsFromTimeRowsOrTimeGroups = (timeRowsOrTimeGroups: TimeRowOrTimeGroup[]): IdsList => {
  return uniq(
    timeRowsOrTimeGroups
      .map(timeRowOrTimeGroup => timeRowOrTimeGroup.tagCategoriesList.map(c => c.id))
      .reduce((acc, tagCategoriesIds) => acc.concat(tagCategoriesIds), [])
  )
}

export const getTagDescription = (tag: Tag.AsObject): string => {
  let tagDesc = tag?.description
  if (tagDesc === undefined || tagDesc === "") {
    tagDesc = t({ id: "label_no_description", message: "No description." })
  }
  return tagDesc
}

export const getTagCategoryDescription = (tagCategory: TagCategory.AsObject): string => {
  let tagCategoryDesc = tagCategory?.description
  if (tagCategoryDesc === undefined || tagCategoryDesc === "") {
    tagCategoryDesc = t({ id: "label_no_description", message: "No description." })
  }
  return tagCategoryDesc
}

export const hasTagChangedForUIRendering = (left: Tag.AsObject | undefined, right: Tag.AsObject | undefined) =>
  left?.name === right?.name
  && left?.description === right?.description
  && left?.suggestionConfig === right?.suggestionConfig
  && left?.defaultActivityTypeId === right?.defaultActivityTypeId
  && equals(left?.keywordsList, right?.keywordsList)

type HasDifferentTagAssigneeParams = {
  tagsList: TagRef.AsObject[],
  tagsCache: TagsCache,
  currentSiteId: SiteId | undefined,
  currentExternalId: string,
  currentUserEmail: string,
}

export const hasDifferentTagAssignee = ({
  tagsList,
  tagsCache,
  currentSiteId,
  currentExternalId,
  currentUserEmail
}: HasDifferentTagAssigneeParams): boolean => tagsList.some(t => {
  const tag: Tag.AsObject | undefined = tagsCache[t.id]
  return (
    tag !== undefined
    && tag.siteId === currentSiteId
    && tag.assigneeExternalIdsList.length !== 0
    && [currentUserEmail, currentExternalId].every(item => !tag.assigneeExternalIdsList.includes(item))
  )
})
